// src/services/api/calendar.ts
// 統一的日曆查詢服務，整合 TimeBox 和 Event

import { TimeBox } from '../../types/timeBox';
import { Event, ScheduleItem } from '../../types';
import * as timeBoxApi from './timeBoxes';
import { eventService } from './calendar-events';
import { getCurrentUserId } from '../auth';

// 統一的日曆項目回調接口
interface CalendarSubscriptionCallbacks {
  onSnapshot: (items: ScheduleItem[]) => void;
  onError: (error: Error) => void;
}

// 將 ScheduleItem 按時間排序
const sortScheduleItems = (items: ScheduleItem[]): ScheduleItem[] => {
  return items.sort((a, b) => {
    const timeA = a.startTime.getTime();
    const timeB = b.startTime.getTime();
    
    if (timeA === timeB) {
      // 如果時間相同，Event 排在 TimeBox 前面
      if ('controllable' in a && 'controllable' in b) {
        return a.controllable === b.controllable ? 0 : (a.controllable ? 1 : -1);
      }
    }
    
    return timeA - timeB;
  });
};

/**
 * 獲取指定日期範圍的所有日程項目 (TimeBox + Event)
 */
const getScheduleItems = async (
  userId: string,
  startDate: Date,
  endDate: Date
): Promise<ScheduleItem[]> => {
  try {
    // 並行查詢 TimeBox 和 Event，但允許部分失敗
    const [timeBoxesResult, eventsResult] = await Promise.allSettled([
      timeBoxApi.getTimeBoxesByDateRange(startDate, endDate, userId),
      eventService.getEventsByDateRange(userId, startDate, endDate)
    ]);

    // 提取成功的結果，忽略失敗的部分
    const timeBoxes: TimeBox[] = timeBoxesResult.status === 'fulfilled' 
      ? timeBoxesResult.value 
      : [];
    const events: Event[] = eventsResult.status === 'fulfilled' 
      ? eventsResult.value 
      : [];

    // 記錄失敗的情況
    if (timeBoxesResult.status === 'rejected') {
      console.error('Failed to load TimeBoxes:', timeBoxesResult.reason);
    }
    if (eventsResult.status === 'rejected') {
      console.error('Failed to load Events:', eventsResult.reason);
    }

    // 合併並排序
    const allItems: ScheduleItem[] = [...timeBoxes, ...events];
    return sortScheduleItems(allItems);
  } catch (error) {
    console.error('Error getting schedule items:', error);
    throw error;
  }
};

/**
 * 獲取指定日期的所有日程項目
 */
const getScheduleItemsByDate = async (
  userId: string,
  date: Date
): Promise<ScheduleItem[]> => {
  const startOfDay = new Date(date);
  startOfDay.setHours(0, 0, 0, 0);
  
  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59, 59, 999);
  
  return getScheduleItems(userId, startOfDay, endOfDay);
};

/**
 * 檢查時間衝突 (跨 TimeBox 和 Event)
 */
const checkScheduleConflicts = async (
  userId: string,
  startTime: Date,
  endTime: Date,
  excludeIds?: { timeBoxIds?: string[], eventIds?: string[] }
): Promise<{
  timeBoxConflicts: TimeBox[];
  eventConflicts: Event[];
  hasConflicts: boolean;
}> => {
  try {
    // 並行檢查 TimeBox 和 Event 衝突
    const [timeBoxConflicts, eventConflicts] = await Promise.all([
      timeBoxApi.checkTimeConflicts(
        startTime, 
        endTime, 
        userId, 
        excludeIds?.timeBoxIds?.[0] // 目前 API 只支援單一排除 ID
      ),
      eventService.checkEventConflicts(
        userId,
        startTime,
        endTime,
        excludeIds?.eventIds?.[0] // 目前 API 只支援單一排除 ID
      )
    ]);

    return {
      timeBoxConflicts,
      eventConflicts,
      hasConflicts: timeBoxConflicts.length > 0 || eventConflicts.length > 0
    };
  } catch (error) {
    console.error('Error checking schedule conflicts:', error);
    throw error;
  }
};

/**
 * 即時監聽指定日期範圍的所有日程項目
 */
const subscribeToScheduleItems = async (
  userId: string,
  startDate: Date,
  endDate: Date,
  callbacks: CalendarSubscriptionCallbacks
): Promise<(() => void)> => {
  let timeBoxes: TimeBox[] = [];
  let events: Event[] = [];
  let timeBoxUnsubscribe: (() => void) | null = null;
  let eventUnsubscribe: (() => void) | null = null;
  let timeBoxInitialized = false;
  let eventInitialized = false;
  
  const updateCallback = () => {
    try {
      
      // 只有當兩個訂閱都初始化後才更新回調
      if (timeBoxInitialized && eventInitialized) {
        const allItems: ScheduleItem[] = [...timeBoxes, ...events];
        const sortedItems = sortScheduleItems(allItems);
        
        
        callbacks.onSnapshot(sortedItems);
      } else {
      }
    } catch (error) {
      console.error('Error in subscribeToScheduleItems updateCallback:', error);
      callbacks.onError(error as Error);
    }
  };

  // 設置 TimeBox 監聽（失敗時不影響整體功能）
  try {
    timeBoxUnsubscribe = await timeBoxApi.subscribeToTimeBoxesByDateRange(
      userId,
      startDate,
      endDate,
      {
        onSnapshot: (newTimeBoxes: any) => {
          timeBoxes = newTimeBoxes;
          timeBoxInitialized = true;
          updateCallback();
        },
        onError: (error) => {
          console.error('TimeBox subscription error:', error);
          // 標記為已初始化以避免阻塞
          timeBoxInitialized = true;
          updateCallback();
        }
      }
    );
  } catch (error) {
    console.error('Error setting up TimeBox subscription:', error);
    // 標記為已初始化以避免阻塞
    timeBoxInitialized = true;
    updateCallback();
  }

  // 設置 Event 監聽（失敗時不影響整體功能）
  try {
    eventUnsubscribe = eventService.subscribeToEventsByDateRange(
      userId,
      startDate,
      endDate,
      {
        onSnapshot: (newEvents) => {
          events = newEvents;
          eventInitialized = true;
          updateCallback();
        },
        onError: (error) => {
          console.error('Event subscription error:', error);
          // 標記為已初始化以避免阻塞
          eventInitialized = true;
          updateCallback();
        }
      }
    );
  } catch (error) {
    console.error('Error setting up Event subscription:', error);
    // 標記為已初始化以避免阻塞
    eventInitialized = true;
    updateCallback();
  }

  // 返回取消函數
  return () => {
    if (timeBoxUnsubscribe) timeBoxUnsubscribe();
    if (eventUnsubscribe) eventUnsubscribe();
  };
};

/**
 * 即時監聽用戶的所有日程項目
 */
const subscribeToAllScheduleItems = async (
  userId: string,
  callbacks: CalendarSubscriptionCallbacks
): Promise<(() => void)> => {
  let timeBoxes: TimeBox[] = [];
  let events: Event[] = [];
  let timeBoxUnsubscribe: (() => void) | null = null;
  let eventUnsubscribe: (() => void) | null = null;
  let timeBoxInitialized = false;
  let eventInitialized = false;
  
  const updateCallback = () => {
    try {
      // 只有當兩個訂閱都初始化後才更新回調
      if (timeBoxInitialized && eventInitialized) {
        const allItems: ScheduleItem[] = [...timeBoxes, ...events];
        const sortedItems = sortScheduleItems(allItems);
        callbacks.onSnapshot(sortedItems);
      }
    } catch (error) {
      console.error('Error in subscribeToAllScheduleItems updateCallback:', error);
      callbacks.onError(error as Error);
    }
  };

  // 設置 TimeBox 監聽（失敗時不影響整體功能）
  try {
    timeBoxUnsubscribe = await timeBoxApi.subscribeToAllTimeBoxes(userId, {
      onSnapshot: (newTimeBoxes: any) => {
        timeBoxes = newTimeBoxes;
        timeBoxInitialized = true;
        updateCallback();
      },
      onError: (error) => {
        console.error('TimeBox subscription error:', error);
        // 標記為已初始化以避免阻塞
        timeBoxInitialized = true;
        updateCallback();
      }
    });
  } catch (error) {
    console.error('Error setting up all TimeBox subscription:', error);
    // 標記為已初始化以避免阻塞
    timeBoxInitialized = true;
    updateCallback();
  }

  // 設置 Event 監聽（失敗時不影響整體功能）
  try {
    eventUnsubscribe = eventService.subscribeToAllEvents(userId, {
      onSnapshot: (newEvents) => {
        events = newEvents;
        eventInitialized = true;
        updateCallback();
      },
      onError: (error) => {
        console.error('Event subscription error:', error);
        // 標記為已初始化以避免阻塞
        eventInitialized = true;
        updateCallback();
      }
    });
  } catch (error) {
    console.error('Error setting up all Event subscription:', error);
    // 標記為已初始化以避免阻塞
    eventInitialized = true;
    updateCallback();
  }

  // 返回取消函數
  return () => {
    if (timeBoxUnsubscribe) timeBoxUnsubscribe();
    if (eventUnsubscribe) eventUnsubscribe();
  };
};

// 導出日曆服務
export const calendarService = {
  getScheduleItems,
  getScheduleItemsByDate,
  checkScheduleConflicts,
  subscribeToScheduleItems,
  subscribeToAllScheduleItems,
};

// 便利函數：使用當前用戶 ID
export const getScheduleItemsForCurrentUser = async (
  startDate: Date,
  endDate: Date
): Promise<ScheduleItem[]> => {
  const userId = getCurrentUserId();
  if (!userId) {
    throw new Error('User not authenticated');
  }
  return getScheduleItems(userId, startDate, endDate);
};

export const getScheduleItemsByDateForCurrentUser = async (
  date: Date
): Promise<ScheduleItem[]> => {
  const userId = getCurrentUserId();
  if (!userId) {
    throw new Error('User not authenticated');
  }
  return getScheduleItemsByDate(userId, date);
};

export const checkScheduleConflictsForCurrentUser = async (
  startTime: Date,
  endTime: Date,
  excludeIds?: { timeBoxIds?: string[], eventIds?: string[] }
) => {
  const userId = getCurrentUserId();
  if (!userId) {
    throw new Error('User not authenticated');
  }
  return checkScheduleConflicts(userId, startTime, endTime, excludeIds);
};